import {
  NaiveDate,
  PartialDate,
  LunarConverter,
  HebrewConverter,
  IslamicConverter,
  PersianConverter,
  ThaiConverter,
  EthiopianConverter,
  IndianConverter,
} from "@tento-chrono";
import { ColumnHeader } from "./cell.ts";

export interface SubHeaderFormatter {
  yearSpans(year: PartialDate, widthPx: number): NaiveDate.Range[];
  yearSpanFormatter(widthPx: number): ColumnHeader.SpanLabelFormatter;
  monthSpanFormatter(): ColumnHeader.SpanLabelFormatter;
  weekDayLabel(date: NaiveDate): string;
}

const gregorian: SubHeaderFormatter = {
  yearSpans(year, widthPx) {
    if (widthPx < 300) return ColumnHeader.getQuartersInYear(year);
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(widthPx) {
    if (widthPx < 300) return ColumnHeader.quarterSpanFormatter;
    if (widthPx < 450) return ColumnHeader.monthAbbrFormatter;
    return ColumnHeader.monthSpanFormatter;
  },
  monthSpanFormatter() {
    return ColumnHeader.weekSpanFormatter;
  },
  weekDayLabel(date) {
    return String(date.day);
  },
};

const lunar: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: LunarConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: LunarConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  weekDayLabel(date) {
    return LunarConverter.labelFor(date) ?? String(date.day);
  },
};

const hebrew: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: HebrewConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: HebrewConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  weekDayLabel(date) {
    return HebrewConverter.labelFor(date) ?? String(date.day);
  },
};

const islamic: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: IslamicConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: IslamicConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  weekDayLabel(date) {
    return IslamicConverter.labelFor(date) ?? String(date.day);
  },
};

const persian: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: PersianConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: PersianConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  weekDayLabel(date) {
    return PersianConverter.labelFor(date) ?? String(date.day);
  },
};

const thai: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: ThaiConverter.labelFor(span.start), sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: ThaiConverter.labelFor(span.start), sublabel: "" });
  },
  weekDayLabel(date) {
    return ThaiConverter.labelFor(date);
  },
};

const ethiopian: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: EthiopianConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: EthiopianConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  weekDayLabel(date) {
    return EthiopianConverter.labelFor(date) ?? String(date.day);
  },
};

const indian: SubHeaderFormatter = {
  yearSpans(year, _widthPx) {
    return ColumnHeader.getMonthsInYear(year).map((m) => m.toRange());
  },
  yearSpanFormatter(_widthPx) {
    return (span) => ({ label: IndianConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  monthSpanFormatter() {
    return (span) => ({ label: IndianConverter.labelFor(span.start) ?? "", sublabel: "" });
  },
  weekDayLabel(date) {
    return IndianConverter.labelFor(date) ?? String(date.day);
  },
};

const FORMATTERS: Record<string, SubHeaderFormatter> = {
  gregorian,
  lunar,
  hebrew,
  islamic,
  persian,
  thai,
  ethiopian,
  indian,
};

export function getSubHeaderFormatter(calendarSystem: string): SubHeaderFormatter {
  return FORMATTERS[calendarSystem] ?? gregorian;
}
